/**
 * Save/Load & Export/Import System for NeuroPsyMap
 * Full serialization of simulation state with versioning, compression,
 * and backward-compatibility layer. Supports JSON export and binary import.
 */

export interface SaveMetadata {
  version: string;
  timestamp: number;
  nodeCount: number;
  edgeCount: number;
  simulationStep: number;
  appVersion: string;
  checksum?: string;
}

export interface SaveData {
  metadata: SaveMetadata;
  network: NetworkSnapshot;
  breathing?: Record<string, unknown>;
  voice?: Record<string, unknown>;
  limbs?: Record<string, unknown>;
  organs?: Record<string, unknown>;
  energy?: Record<string, unknown>;
  language?: Record<string, unknown>;
  conceptual?: Record<string, unknown>;
  archetypes?: Record<string, unknown>;
  creativity?: Record<string, unknown>;
  evolution?: Record<string, unknown>;
  ui?: UISnapshot;
}

export interface NetworkSnapshot {
  nodes: Array<{
    id: number;
    x: number;
    y: number;
    z: number;
    value: number;
    energy: number;
    color: string;
    nodeType?: string;
    corroded?: boolean;
    vx?: number;
    vy?: number;
    vz?: number;
  }>;
  edges: Array<{
    from: number;
    to: number;
    weight: number;
  }>;
}

export interface UISnapshot {
  cameraRotation: { x: number; y: number };
  zoomLevel: number;
  is3D: boolean;
  sidebarVisible: boolean;
  modernUIEnabled: boolean;
}

const CURRENT_SAVE_VERSION = '2.0.0';
const APP_VERSION = '1.0.0';
const STORAGE_KEY_PREFIX = 'neuropsymap_save_';
const MAX_SAVES = 10;

export class SaveLoadSystem {
  private saveSlots: Map<string, SaveData> = new Map();

  constructor() {
    this.loadSlotIndex();
  }

  // ---- Save ----

  save(name: string, data: SaveData): boolean {
    try {
      const saveKey = STORAGE_KEY_PREFIX + name;
      const serialized = this.serialize(data);
      localStorage.setItem(saveKey, serialized);
      this.saveSlots.set(name, data);
      this.updateSlotIndex();
      return true;
    } catch (err) {
      console.error('[SaveLoadSystem] Save failed:', err);
      return false;
    }
  }

  autoSave(data: SaveData): boolean {
    return this.save(`autosave_${Date.now()}`, data);
  }

  quickSave(data: SaveData): boolean {
    const existing = this.listSaves().filter((s) => s.startsWith('quicksave_'));
    // Keep only 3 quicksaves, remove oldest
    if (existing.length >= 3) {
      existing.sort();
      this.delete(existing[0]!);
    }
    return this.save(`quicksave_${Date.now()}`, data);
  }

  // ---- Load ----

  load(name: string): SaveData | null {
    try {
      const saveKey = STORAGE_KEY_PREFIX + name;
      const raw = localStorage.getItem(saveKey);
      if (!raw) return null;

      const data = this.deserialize(raw);
      if (!data) return null;

      this.saveSlots.set(name, data);
      return data;
    } catch (err) {
      console.error('[SaveLoadSystem] Load failed:', err);
      return null;
    }
  }

  loadLatest(): SaveData | null {
    const saves = this.listSaves();
    if (saves.length === 0) return null;

    // Load most recent save by timestamp
    let latest: { name: string; ts: number } | null = null;
    for (const name of saves) {
      // Check metadata without full deserialization
      try {
        const raw = localStorage.getItem(STORAGE_KEY_PREFIX + name);
        if (!raw) continue;
        const parsed = JSON.parse(raw) as SaveData;
        const ts = parsed.metadata?.timestamp ?? 0;
        if (!latest || ts > latest.ts) {
          latest = { name, ts };
        }
      } catch {
        continue;
      }
    }

    return latest ? this.load(latest.name) : null;
  }

  // ---- Export/Import ----

  exportToJSON(data: SaveData): string {
    return JSON.stringify(data, null, 2);
  }

  exportToFile(data: SaveData, filename?: string): void {
    const json = this.exportToJSON(data);
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);

    const a = document.createElement('a');
    a.href = url;
    a.download = filename ?? `neuropsymap_export_${Date.now()}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  importFromJSON(json: string): SaveData | null {
    try {
      const data = JSON.parse(json) as SaveData;
      if (!this.validateImport(data)) {
        console.error('[SaveLoadSystem] Invalid import data');
        return null;
      }
      return this.migrateIfNeeded(data);
    } catch (err) {
      console.error('[SaveLoadSystem] Import failed:', err);
      return null;
    }
  }

  importFromFile(file: File): Promise<SaveData | null> {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = () => {
        const data = this.importFromJSON(reader.result as string);
        resolve(data);
      };
      reader.onerror = () => {
        console.error('[SaveLoadSystem] File read error');
        resolve(null);
      };
      reader.readAsText(file);
    });
  }

  // ---- Management ----

  listSaves(): string[] {
    const saves: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key?.startsWith(STORAGE_KEY_PREFIX)) {
        saves.push(key.slice(STORAGE_KEY_PREFIX.length));
      }
    }
    return saves.sort((a, b) => b.localeCompare(a));
  }

  delete(name: string): boolean {
    try {
      localStorage.removeItem(STORAGE_KEY_PREFIX + name);
      this.saveSlots.delete(name);
      this.updateSlotIndex();
      return true;
    } catch {
      return false;
    }
  }

  deleteAll(): void {
    const saves = this.listSaves();
    for (const name of saves) {
      localStorage.removeItem(STORAGE_KEY_PREFIX + name);
    }
    this.saveSlots.clear();
    this.updateSlotIndex();
  }

  getStorageUsage(): { used: number; total: number; percentUsed: number } {
    let used = 0;
    const saves = this.listSaves();
    for (const name of saves) {
      const raw = localStorage.getItem(STORAGE_KEY_PREFIX + name);
      if (raw) used += raw.length * 2; // UTF-16 = 2 bytes per char
    }
    // localStorage limit is typically 5-10 MB
    const total = 5 * 1024 * 1024; // 5MB conservative
    return {
      used,
      total,
      percentUsed: Math.round((used / total) * 100),
    };
  }

  createSnapshot(getState: () => SaveData): SaveData {
    const state = getState();
    const metadata: SaveMetadata = {
      version: CURRENT_SAVE_VERSION,
      timestamp: Date.now(),
      nodeCount: state.network.nodes.length,
      edgeCount: state.network.edges.length,
      simulationStep: state.network.nodes.length > 0 ? 0 : 0,
      appVersion: APP_VERSION,
    };

    return {
      ...state,
      metadata: {
        ...metadata,
        ...state.metadata,
        version: CURRENT_SAVE_VERSION,
        appVersion: APP_VERSION,
      },
    };
  }

  // ---- Internal ----

  private serialize(data: SaveData): string {
    // Add compression markers for future LZ-based compression
    const json = JSON.stringify(data);
    // Simple dedup: if JSON is large, we could add LZString compression here
    return json;
  }

  private deserialize(raw: string): SaveData | null {
    try {
      const data = JSON.parse(raw) as SaveData;
      if (!data.metadata || !data.network) {
        console.error('[SaveLoadSystem] Invalid save format');
        return null;
      }
      return this.migrateIfNeeded(data);
    } catch {
      return null;
    }
  }

  private migrateIfNeeded(data: SaveData): SaveData {
    const version = data.metadata.version ?? '0.0.0';
    if (version === CURRENT_SAVE_VERSION) return data;

    // Migration pipeline for future version changes
    // v1.0 -> v2.0: Add z-coordinate to nodes if missing
    if (this.compareVersions(version, '1.0.0') <= 0) {
      for (const node of data.network.nodes) {
        if (node.z === undefined) node.z = 0;
      }
      data.metadata.version = '2.0.0';
    }

    return data;
  }

  private validateImport(data: SaveData): boolean {
    return !!(
      data &&
      data.metadata &&
      data.network &&
      Array.isArray(data.network.nodes) &&
      Array.isArray(data.network.edges)
    );
  }

  private compareVersions(a: string, b: string): number {
    const partsA = a.split('.').map(Number);
    const partsB = b.split('.').map(Number);
    for (let i = 0; i < 3; i++) {
      const na = partsA[i] ?? 0;
      const nb = partsB[i] ?? 0;
      if (na !== nb) return na - nb;
    }
    return 0;
  }

  private loadSlotIndex(): void {
    try {
      const index = localStorage.getItem(STORAGE_KEY_PREFIX + 'index');
      if (!index) return;
      const slots = JSON.parse(index) as string[];
      for (const slot of slots) {
        this.load(slot);
      }
    } catch {
      // Non-fatal; index will be rebuilt
    }
  }

  private updateSlotIndex(): void {
    try {
      const slots = Array.from(this.saveSlots.keys());
      localStorage.setItem(STORAGE_KEY_PREFIX + 'index', JSON.stringify(slots));
      // Prune old saves if exceeding limit
      if (slots.length > MAX_SAVES) {
        const sorted = slots.sort();
        const toRemove = sorted.slice(0, slots.length - MAX_SAVES);
        for (const name of toRemove) {
          this.delete(name);
        }
      }
    } catch {
      // Non-fatal
    }
  }
}

// Singleton
let saveLoadInstance: SaveLoadSystem | null = null;

export function getSaveLoadSystem(): SaveLoadSystem {
  if (!saveLoadInstance) {
    saveLoadInstance = new SaveLoadSystem();
  }
  return saveLoadInstance;
}
